package ui.app.rss.instance.detail


import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.grid.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.*
import androidx.compose.ui.graphics.vector.*
import androidx.compose.ui.layout.*
import androidx.compose.ui.unit.*
import api.*
import compose.assets.*
import compose.assets.svg.*
import org.jetbrains.compose.resources.*
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.*
import tk.mallumo.utils.*
import tools.*
import ui.resource.*
import ui.widget.*

data class AppRssDetailInstanceArgs(
    var id: Long = -1L
) {
    val isCreator get() = id == creator.id

    companion object {
        val creator get() = AppRssDetailInstanceArgs(-1L)
    }
}

@ComposableNavNode(AppRssDetailInstanceArgs::class)
@Composable
fun AppRssDetailInstanceUI() {
    with(AppRssDetailInstanceScope.remember()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> Content(state.nnvl)
        }

    }
}

fun AppRssDetailInstanceScope.openResourcePicker(key: String, nnvl: AppRssDetailInstanceScope.Wrapper) {
    GlobalObjectShare.launchImagePicker(
        key = key,
        nav = nav,
        args = ResourceListingArgs(
            companyId = user.company.id,
            restrictionType = ResourceType.IMAGE.mask,
            restrictionCanIgnoreAspectRatio = true
        )
    ) {
        it.firstOrNull()?.apply {
            nnvl.logo.set(id)
        }
    }
}

@Composable
private fun AppRssDetailInstanceScope.Content(nnvl: AppRssDetailInstanceScope.Wrapper) {
    val deleteDialogState = DialogState.remember()
    DeleteDialog(deleteDialogState, nnvl)
    val toolbarActions = remember(vm.state, args.isCreator) {
        buildList {
            add(ToolbarAction.save(nnvl.changed) {
                action(AppRssDetailInstanceVM.Action.Save)
            })
            if (!args.isCreator) {
                add(ToolbarAction.delete {
                    deleteDialogState.show()
                })
            }
        }
    }

    val imagePickerKey = remember(args.id) {
        "AppRssDetailInstanceUI:${args.id}"
    }

    GlobalObjectShare.Consumer(imagePickerKey)

    Column(Modifier.fillMaxSize()) {
        Toolbar(
            nav = nav,
            titlePrefix = Res.string.layer_bottom_rss_instance_title.rememberString(),
            titleSuffix = if (args.isCreator) Res.string.layer_bottom_rss_instance_title_create.rememberString()
            else nnvl.data.item.id.toString(),
            actions = toolbarActions
        )
        LazyVerticalGrid(
            columns = if (display.resolution.width < 780) GridCells.Fixed(1)
            else GridCells.FixedSize(384.dp),
            contentPadding = PaddingValues(8.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            modifier = Modifier.fillMaxSize()
        ) {
            item {
                FieldTextString(
                    icon = Svg.Format_text,
                    label = Res.string.layer_bottom_rss_instance_label_name,
                    field = nnvl.name,
                )
            }
            item {
                FieldTextString(
                    icon = Svg.Text_long,
                    label = Res.string.layer_bottom_rss_instance_label_description,
                    field = nnvl.description,
                )
            }

            item {
                FieldChannelPicker(nnvl)
            }
            item(
                span = {
                    GridItemSpan(maxCurrentLineSpan)
                },
                content = {})

            item {
                FieldIcon(nnvl, imagePickerKey)

            }
            item {
                FieldSwitchersPrimary(nnvl)
            }
            item {
                FieldSwitchersSecondary(nnvl)
            }
            item(
                span = { GridItemSpan(maxCurrentLineSpan) },
                content = {})
            item {
                FieldTextString(
                    icon = Svg.Search,
                    label = Res.string.layer_bottom_editor__rss_label_search,
                    field = nnvl.labelSearch,
                )
            }
            item {
                FieldTextString(
                    icon = Svg.Filter,
                    label = Res.string.layer_bottom_editor__rss_label_filter_category,
                    field = nnvl.labelCategory,
                )
            }

            item {
                FieldTextString(
                    icon = Svg.Text_long,
                    label = Res.string.layer_bottom_editor__rss_label_item_description,
                    field = nnvl.labelItemDescription,
                )
            }
            item {
                FieldTextString(
                    icon = Svg.Text_long,
                    label = Res.string.layer_bottom_editor__rss_label_item_category,
                    field = nnvl.labelItemCategory,
                )
            }
            item {
                FieldTextString(
                    icon = Svg.Text_long,
                    label = Res.string.layer_bottom_editor__rss_label_item_author,
                    field = nnvl.labelItemAuthor,
                )
            }
            item {
                FieldTextString(
                    icon = Svg.Text_long,
                    label = Res.string.layer_bottom_editor__rss_label_item_details,
                    field = nnvl.labelItemDetails,
                )
            }
            item {
                FieldTextString(
                    icon = Svg.Text_long,
                    label = Res.string.layer_bottom_editor__rss_label_item_published,
                    field = nnvl.labelItemPublished,
                )
            }
            item {
                FieldTextString(
                    icon = Svg.Text_long,
                    label = Res.string.layer_bottom_editor__rss_label_item_valid_until,
                    field = nnvl.labelItemValidUntil,
                )
            }
            item {
                FieldTextString(
                    icon = Svg.Text_long,
                    label = Res.string.layer_bottom_editor__rss_label_item_attachments,
                    field = nnvl.labelItemAttachments,
                )
            }
        }
    }
}

@Composable
fun AppRssDetailInstanceScope.DeleteDialog(deleteDialogState: DialogState, nnvl: AppRssDetailInstanceScope.Wrapper) {
    Dialog(deleteDialogState) {
        DialogUI(
            modifier = Modifier.align(Alignment.Center),
            onDismissRequest = { deleteDialogState.hide() },
            title = {
                Text(Res.string.layer_bottom_rss_instance_delete_title.rememberString())
            },
            dismissButton = {
                TextButton({ deleteDialogState.hide() }) {
                    Text(Res.string.dialog__action_cancel.rememberString())
                }
            },
            confirmButton = {
                if (nnvl.data.item.canDelete) {
                    TextButton({
                        action(AppRssDetailInstanceVM.Action.Delete)
                        deleteDialogState.hide()
                    }) {
                        Text(Res.string.dialog__action_ok.rememberString())
                    }
                }
            }) {
            if (nnvl.data.item.canDelete) {
                Text(Res.string.layer_bottom_rss_instance_delete_msg_ok.rememberString())
            } else {
                Text(Res.string.layer_bottom_rss_instance_delete_msg_err.rememberString())
            }
        }
    }
}

@Composable
fun AppRssDetailInstanceScope.FieldIcon(nnvl: AppRssDetailInstanceScope.Wrapper, key: String) {
    Card({ openResourcePicker(key, nnvl) }) {
        Box(Modifier.fillMaxWidth().padding(8.dp)) {
            Text(
                text = Res.string.layer_bottom_rss_instance_label_icon.rememberString(),
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurface
            )
            LazyImage(
                id = nnvl.logo.state,
                modifier = Modifier.size(192.dp)
                    .align(Alignment.Center),
                contentScale = ContentScale.FillBounds
            )
        }
    }
}

@Composable
fun FieldSwitchersPrimary(nnvl: AppRssDetailInstanceScope.Wrapper) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        FieldSwitch(
            title = Res.string.layer_bottom_rss_instance_label_listing_image_title,
            message = Res.string.layer_bottom_rss_instance_label_listing_image_msg,
            field = nnvl.showListingImage
        )

        FieldSwitch(
            title = Res.string.layer_bottom_rss_instance_label_listing_title_visibility_title,
            message = Res.string.layer_bottom_rss_instance_label_listing_title_visibility_msg,
            field = nnvl.showListingTitle
        )

        FieldSwitch(
            title = Res.string.layer_bottom_rss_instance_label_listing_desc_title,
            message = Res.string.layer_bottom_rss_instance_label_listing_desc_msg,
            field = nnvl.showListingDescription
        )
    }
}

@Composable
fun FieldSwitchersSecondary(nnvl: AppRssDetailInstanceScope.Wrapper) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        FieldSwitch(
            title = Res.string.layer_bottom_rss_instance_label_detail_visibility_title,
            message = Res.string.layer_bottom_rss_instance_label_detail_visibility_msg,
            field = nnvl.showDetail
        )

        FieldSwitch(
            title = Res.string.layer_bottom_rss_instance_label_listing_desc_as_html_title,
            message = Res.string.layer_bottom_rss_instance_label_listing_desc_as_html_msg,
            field = nnvl.showListingDescriptionAsHtml,
            enabled = nnvl.showListingDescription.state
        )

        FieldSwitch(
            title = Res.string.layer_bottom_rss_instance_label_detail_desc_as_html_title,
            message = Res.string.layer_bottom_rss_instance_label_detail_desc_as_html_msg,
            field = nnvl.showItemDescriptionAsHtml,
            enabled = nnvl.showDetail.state
        )
    }
}

@Composable
private fun FieldChannelPicker(nnvl: AppRssDetailInstanceScope.Wrapper) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Text(
                text = Res.string.layer_bottom_rss_instance_label_channel.rememberString(),
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurface,
                modifier = Modifier.padding(start = 8.dp)
            )
            DropDownPicker(
                selected = nnvl.selectedChannel,
                values = nnvl.channels,
                itemWidth = 384.dp,
                modifier = Modifier.fillMaxWidth(),
                previewImage = null,
                previewLabel = null,
                enabled = true
            ) {
                nnvl.channelId.set(it.channel.id)
            }
        }
    }
}

@Composable
private fun FieldSwitch(
    title: StringResource,
    message: StringResource,
    field: EFieldRegularBoolean,
    enabled: Boolean = true
) {
    Card(
        onClick = { field.set(!field.state) },
        enabled = enabled
    ) {
        Row(
            Modifier.fillMaxWidth()
                .padding(
                    horizontal = 8.dp,
                    vertical = 4.dp
                ),
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            IconicSwitch(
                checked = field.state,
                enabled = enabled,
                onCheckedChange = { field.set(it) },
                modifier = Modifier.align(Alignment.CenterVertically)
            )
            Column(Modifier.align(Alignment.CenterVertically)) {
                Text(
                    text = title.rememberString(),
                    style = MaterialTheme.typography.bodyMedium
                )
                Text(
                    text = message.rememberString(),
                    style = MaterialTheme.typography.labelSmall
                )
            }
        }
    }
}


@Composable
private fun FieldTextString(
    icon: ImageVector,
    field: EFieldTextString,
    label: StringResource
) {
    TextField(
        value = field.text,
        isError = field.err,
        onValueChange = { field.set(it) },
        modifier = Modifier.fillMaxWidth(),
        leadingIcon = { Icon(icon, "") },
        label = { Text(label.rememberString()) })
}
